import React, {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { DateTime } from 'luxon'
import { getParameterByName, sendGA4, shuffleArray } from '../../utils'
import { usePage } from '../../contexts'
import { isNativeApp } from '../../client'
import { useRouter } from 'next/router'
import {
  NextArticleLoader,
  RightFixedMobileButton,
} from './NextItemPager.styles'
import { NextItemArrow } from './NextItemArrow'

type props = {
  objectToSlide: RefObject<HTMLElement>
}

class Cookies {
  set = (name: string, value: string, minutes: number) => {
    const expires = DateTime.now().plus({ minutes }).toUTC().toHTTP()
    document.cookie = `${name}=${value};expires=${expires};path=/`
  }
  get = (name: string) => {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift()
    }
    return null
  }
}

export const NextItemPager = ({ objectToSlide }: props) => {
  const router = useRouter()
  const nextPages = usePage()
  const [showLoader, setShowLoader] = useState<boolean>(false)
  const rightFixedMobileButtonRef = useRef<HTMLDivElement>(null)
  const [nextPageLink, setNextPageLink] = useState<null | string>(null)
  const [storageTimeOut, setStorageTimeOut] = useState<null | number>(null)
  const [arrowHeight] = useState(62)

  useEffect(() => {
    const pagerTimeOut =
      getParameterByName('pagerTimeOut') ||
      sessionStorage.getItem('pagerTimeOut') ||
      '20'
    sessionStorage.setItem('pagerTimeOut', pagerTimeOut.toString())
    setStorageTimeOut(Math.max(20, parseInt(pagerTimeOut)))
  }, [])

  const cookieExpired = useCallback(() => {
    if (!storageTimeOut) {
      return false
    }
    const cookieName = 'nextItemTTL'
    const currentDateTime = new Date()
    const cookie = new Cookies()
    const cookieValue = cookie.get(cookieName)
    if (cookieValue) {
      const cookieDateTime = new Date(cookieValue)
      const timeDifference =
        (currentDateTime.getTime() - cookieDateTime.getTime()) / 60000

      if (timeDifference > storageTimeOut) {
        cookie.set(cookieName, currentDateTime.toISOString(), storageTimeOut)
        return true
      } else {
        return false
      }
    } else {
      cookie.set(cookieName, currentDateTime.toISOString(), storageTimeOut)
      return true
    }
  }, [storageTimeOut])

  useEffect(() => {
    const localStorageItems = localStorage.getItem('itemLinks')
    if (localStorageItems) {
      const items = JSON.parse(localStorageItems) as string[]
      if (items.length > 0) {
        items.filter(link => link !== router.asPath)
        setNextPageLink(items[0])
      }
    } else {
      setNextPageLink(null)
    }
  }, [router.asPath])

  useEffect(() => {
    if (nextPageLink) {
      router.prefetch(nextPageLink)
    }
  }, [nextPageLink, router])

  useEffect(() => {
    if (cookieExpired()) {
      localStorage.removeItem('itemLinks')
      if (
        nextPages?.Content.nextItemLinks &&
        nextPages.Content.nextItemLinks.length > 0
      ) {
        const itemLinks = shuffleArray(
          nextPages?.Content.nextItemLinks.filter(
            link => link !== window.location.pathname
          )
        )
        localStorage.setItem('itemLinks', JSON.stringify(itemLinks))
        if (rightFixedMobileButtonRef.current) {
          rightFixedMobileButtonRef.current.style.display = 'block'
        }
      } else {
        if (rightFixedMobileButtonRef.current) {
          rightFixedMobileButtonRef.current.style.display = 'none'
        }
        fetch('/Services/homepage_items.json')
          .then(res => res.json())
          .then(links => {
            if (links) {
              const itemLinks = shuffleArray(
                (links as string[]).filter(link => {
                  return link !== window.location.pathname
                })
              )
              localStorage.setItem('itemLinks', JSON.stringify(itemLinks))
              if (itemLinks && itemLinks.length > 0) {
                setNextPageLink(itemLinks[0] || null)
              }
              if (rightFixedMobileButtonRef.current) {
                rightFixedMobileButtonRef.current.style.display = 'block'
              }
            }
          })
      }
    }
  }, [cookieExpired, nextPages.Content.nextItemLinks, router.asPath])

  const setDailyClicksCount = (value: number) => {
    sessionStorage.setItem('ClicksCount', String(value))
  }

  const getDailyClicksCount = (): number => {
    const clicksCount = sessionStorage.getItem('ClicksCount')
    return Number(clicksCount)
  }

  const nextItem = () => {
    if (nextPageLink) {
      if (rightFixedMobileButtonRef.current) {
        rightFixedMobileButtonRef.current.style.display = 'none'
      }
      shiftItems()
      slideLeft()
      setShowLoader(true)
      router.push(nextPageLink, undefined)

      const clicksCount = getDailyClicksCount()
      setDailyClicksCount(clicksCount + 1)

      sendGA4({
        eventName: isNativeApp() ? 'app_daily_click' : 'daily_click',
        pageData: nextPages,
        clean_page_url: window.location.href.split('?')[0],
        num: getDailyClicksCount(),
      })

      if (rightFixedMobileButtonRef.current) {
        rightFixedMobileButtonRef.current.style.display = 'none'
      }
    } else {
      setDailyClicksCount(0)
    }
  }

  const slideLeft = () => {
    if (objectToSlide.current) {
      objectToSlide.current.style.transition = 'transform 1s ease'
      objectToSlide.current.style.transform = 'translateX(-900px)'
    }
  }

  const shiftItems = () => {
    const localStorageItems = localStorage.getItem('itemLinks')
    if (localStorageItems) {
      const items = JSON.parse(localStorageItems) as string[]
      items.shift()
      localStorage.setItem('itemLinks', JSON.stringify(items))
    }
  }

  if (!nextPageLink) {
    return null
  }

  return (
    <>
      <RightFixedMobileButton
        ref={rightFixedMobileButtonRef}
        onClick={() => nextItem()}
        height={arrowHeight}
      >
        <NextItemArrow height={arrowHeight} />
      </RightFixedMobileButton>
      {showLoader && <NextArticleLoader />}
    </>
  )
}

export default NextItemPager
